import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  GraduationCap, 
  Users, 
  Target, 
  Compass,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const WORKFORCE_IMAGE = "https://images.unsplash.com/flagged/photo-1576485436509-a7d286952b65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHx3b3JrZm9yY2UlMjB0cmFpbmluZyUyMHByb2Zlc3Npb25hbCUyMG1lZXRpbmd8ZW58MHx8fHwxNzczMjQ5NTc1fDA&ixlib=rb-4.1.0&q=85";

const initiatives = [
  {
    icon: GraduationCap,
    title: "Education & Orientation",
    description: "Providing information and orientation to help individuals understand opportunities within the behavioral health support ecosystem."
  },
  {
    icon: Users,
    title: "Community Engagement",
    description: "Encouraging participation in community support roles and connecting individuals with behavioral health service networks."
  },
  {
    icon: Target,
    title: "Career Pathways",
    description: "Helping individuals understand various career paths within behavioral health support and how to pursue them."
  },
  {
    icon: Compass,
    title: "Resource Navigation",
    description: "Guiding individuals to training programs, certifications, and opportunities in the behavioral health field."
  },
];

const WorkforceEmpowerment = () => {
  return (
    <div className="min-h-screen" data-testid="workforce-empowerment-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
                Workforce Empowerment
              </h1>
              <p className="text-lg text-white/90 leading-relaxed">
                A strong behavioral health support system requires a knowledgeable and compassionate workforce. We promote workforce empowerment by encouraging individuals interested in community support roles to engage with behavioral health service networks.
              </p>
            </div>
            <div className="hidden md:block">
              <img 
                src={WORKFORCE_IMAGE} 
                alt="Workforce training session" 
                className="rounded-2xl shadow-2xl object-cover w-full h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
              Building the Support Workforce
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Through guidance, orientation, and community engagement initiatives, the agency supports individuals who wish to contribute to strengthening behavioral health support systems. Our workforce empowerment initiatives focus on helping individuals understand opportunities within the behavioral health support ecosystem and encouraging participation in community support roles.
            </p>
          </div>
        </div>
      </section>

      {/* Initiatives Grid */}
      <section className="py-20 bg-brand-neutral">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-brand-blue mb-4">
              Our Initiatives
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We offer various programs and resources to help build and strengthen the behavioral health workforce.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {initiatives.map((initiative, index) => {
              const Icon = initiative.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-100 hover:border-brand-teal"
                  data-testid={`initiative-card-${index}`}
                >
                  <CardContent className="p-0 flex gap-6">
                    <div className="w-14 h-14 bg-brand-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon size={28} className="text-brand-teal" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-brand-blue mb-3">
                        {initiative.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {initiative.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-br from-brand-orange to-brand-teal p-8 rounded-2xl text-white">
              <h3 className="font-heading text-2xl font-bold mb-6">
                Why Join the Behavioral Health Workforce?
              </h3>
              <ul className="space-y-4">
                {[
                  "Make a meaningful difference in people's lives",
                  "Be part of a growing and essential field",
                  "Connect with a supportive community of professionals",
                  "Access ongoing learning and development opportunities",
                  "Contribute to stronger community support systems"
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
                Interested in Getting Involved?
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Whether you're looking to start a career in behavioral health support or want to learn more about opportunities in this field, we can help guide you toward the right resources and pathways.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our team can provide information about training programs, volunteer opportunities, and career paths within the behavioral health support ecosystem.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-6 py-3"
                    data-testid="contact-btn"
                  >
                    Contact Us
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/partner-with-us">
                  <Button 
                    variant="outline" 
                    className="border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white rounded-full px-6 py-3"
                    data-testid="partner-btn"
                  >
                    Partner With Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkforceEmpowerment;
